import { Input, Switch, Text, Textarea, toast } from "@medusajs/ui"
import { useEffect, useState } from "react"
import { Field } from "@/components/form"
import { KlaviyoCard } from "@/components/klaviyo"
import { ConversionsApiCard } from "@/components/conversions-api"
import { AdSpendCard } from "@/components/ad-spend-settings"
import { ErrorState, SaveBar, Section } from "@/components/ui"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"
import { usePrivacySettings, useTrackingSettings, type PrivacySettings, type TrackingSettings } from "@/lib/settings"
import { SettingsTitle } from "./settings-general"

const IDS = {
  meta_pixel_id: /^\d{8,20}$/,
  ga4_id: /^G-[A-Z0-9]{6,14}$/,
  tiktok_pixel_id: /^[A-Z0-9]{10,30}$/,
}

/** Settings → Customer events: marketing pixel IDs. Only IDs are accepted, never script code. */
export function TrackingSettingsPage() {
  const { get, save } = useTrackingSettings()
  const writable = useCan()("settings:write")
  const [f, setF] = useState<TrackingSettings | null>(null)
  useEffect(() => { if (get.data) setF(get.data.settings) }, [get.data])
  if (get.error) return <ErrorState error={get.error} />
  if (!f || !get.data) return <Text size="small">Loading…</Text>
  const bad = (k: keyof TrackingSettings) => f[k].trim() !== "" && !IDS[k].test(f[k].trim())
  const invalid = bad("meta_pixel_id") || bad("ga4_id") || bad("tiktok_pixel_id")
  const dirty = JSON.stringify(f) !== JSON.stringify(get.data.settings)
  const row = (k: keyof TrackingSettings, label: string, placeholder: string, hint: string) => (
    <Field label={label} hint={bad(k) ? "That doesn't look like a valid ID." : hint}>
      <Input value={f[k]} disabled={!writable} placeholder={placeholder} maxLength={40} aria-invalid={bad(k)} onChange={(e) => setF({ ...f, [k]: e.target.value.trim() })} />
    </Field>
  )
  return (
    <div className="flex flex-col gap-4 pb-24">
      <SettingsTitle title="Customer events" />
      <KlaviyoCard />
      <Section title="Marketing pixels">
        <div className="flex flex-col gap-4 p-4">
          {row("meta_pixel_id", "Meta (Facebook) pixel ID", "1234567890123456", "Digits only. Tracks PageView, ViewContent, AddToCart, InitiateCheckout, Purchase and Lead (newsletter sign-ups).")}
          {row("ga4_id", "Google Analytics 4 measurement ID", "G-XXXXXXXXXX", "Starts with G-.")}
          {row("tiktok_pixel_id", "TikTok pixel ID", "C1A2B3C4D5E6F7G8H9", "Capital letters and digits.")}
          <Text size="xsmall" className="text-ui-fg-subtle">
            Scripts load from each vendor directly. If the cookie banner is on, they only load after the customer accepts.
          </Text>
        </div>
      </Section>
      <ConversionsApiCard />
      <AdSpendCard />
      <SaveBar visible={writable && dirty} message="Unsaved changes" onDiscard={() => setF(get.data.settings)} onSave={() => (invalid ? toast.error("Fix the highlighted IDs first") : save.mutate(f, { onSuccess: () => toast.success("Customer events saved"), onError: (e) => toast.error(errorMessage(e)) }))} saving={save.isPending} />
    </div>
  )
}

/** Settings → Customer privacy: cookie banner. */
export function PrivacySettingsPage() {
  const { get, save } = usePrivacySettings()
  const writable = useCan()("settings:write")
  const [f, setF] = useState<PrivacySettings | null>(null)
  useEffect(() => { if (get.data) setF(get.data.settings) }, [get.data])
  if (get.error) return <ErrorState error={get.error} />
  if (!f || !get.data) return <Text size="small">Loading…</Text>
  const dirty = JSON.stringify(f) !== JSON.stringify(get.data.settings)
  const badUrl = f.policy_url !== "" && !/^(https:\/\/|\/(?![/\\]))/i.test(f.policy_url)
  return (
    <div className="flex flex-col gap-4 pb-24">
      <SettingsTitle title="Customer privacy" />
      <Section title="Cookie banner">
        <div className="flex flex-col gap-4 p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Text size="small" weight="plus">Ask visitors before tracking</Text>
              <Text size="small" className="mt-1 text-ui-fg-subtle">Shows a banner with Accept and Decline. Pixels stay off until the visitor accepts.</Text>
            </div>
            <Switch checked={f.banner} disabled={!writable} onCheckedChange={(banner) => setF({ ...f, banner })} aria-label="Cookie banner" />
          </div>
          <Field label="Banner message" hint={`${f.message.length} of 300 characters`}>
            <Textarea rows={3} maxLength={300} value={f.message} disabled={!writable} onChange={(e) => setF({ ...f, message: e.target.value })} />
          </Field>
          <Field label="Privacy policy link" hint={badUrl ? "Use a path like /pages/privacy-policy or an https:// link." : "Shown as “Privacy policy” in the banner."}>
            <Input value={f.policy_url} disabled={!writable} aria-invalid={badUrl} onChange={(e) => setF({ ...f, policy_url: e.target.value })} />
          </Field>
        </div>
      </Section>
      <SaveBar visible={writable && dirty} message="Unsaved changes" onDiscard={() => setF(get.data.settings)} onSave={() => (badUrl ? toast.error("Fix the privacy policy link first") : save.mutate(f, { onSuccess: () => toast.success("Privacy settings saved"), onError: (e) => toast.error(errorMessage(e)) }))} saving={save.isPending} />
    </div>
  )
}
